import {
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CaFullFormExternalData,
  CaFullFormField,
  CaFullFormValue,
  CaType,
  formValueToDto,
} from './ca-full-form.types';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { MatButtonModule } from '@angular/material/button';
import { CONFIG } from './ca-full-form.config';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  combineLatestWith,
  distinctUntilChanged,
  map,
  merge,
  startWith,
  tap,
} from 'rxjs';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { certificateMaxValidityYearsNotExceedsValidityYears } from './ca-full-form.validators';
import { Profile } from 'src/app/shared/entities/profile';
import { CA } from 'src/app/shared/entities/ca';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { CreateCaFullDto } from 'src/app/shared/services/api/dto/create-ca-full-dto';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { signingAlgos } from 'src/app/shared/entities/signing-algos';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';

@Component({
  selector: 'app-ca-full-form',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    FormFieldComponent,
    FormFieldsDirective,
  ],
  providers: [FormFieldService],
  templateUrl: './ca-full-form.html',
  styleUrl: './ca-full-form.scss',
})
export class CaFullFormComponent {
  profiles = input<Profile[]>([]);
  cas = input<CA[]>([]);
  cryptotokens = input<CryptoToken[]>([]);
  canceled = output<void>();
  created = output<CreateCaFullDto>();

  private dialogService = inject(DialogService);
  private langService = inject(LangService);
  private config = CONFIG({ lang: this.langService.lang });
  formFieldService = inject(FormFieldService);
  private fb = inject(FormBuilder);
  CaFullFormField = CaFullFormField;
  signingAlgos = signingAlgos;

  form = this.fb.group({
    [CaFullFormField.caType]: [CaType.ICA, [Validators.required]],
    [CaFullFormField.caCertificateProfile]: [null, [Validators.required]],
    [CaFullFormField.profileName]: [
      null,
      [Validators.pattern(ValidationConstants.profile.namePattern)],
    ],
    [CaFullFormField.dn]: [null, [Validators.required]],
    [CaFullFormField.validityYears]: [null, [Validators.required]],
    [CaFullFormField.cryptoTokenName]: [null, []],
    [CaFullFormField.password]: [null, []],
    [CaFullFormField.cryptoTokenType]: [null, [Validators.required]],
    [CaFullFormField.signedBy]: [null, [Validators.required]],
    [CaFullFormField.signingAlgo]: [null, [Validators.required]],
    [CaFullFormField.keySpecification]: [null, [Validators.required]],
    [CaFullFormField.certificateMaxValidityYears]: [
      null,
      [Validators.required, certificateMaxValidityYearsNotExceedsValidityYears],
    ],
    [CaFullFormField.useLdapDnOrder]: [false, []],
  });

  labels = this.langService.lang.features.provisionOperationalCa;

  private externalData = computed<CaFullFormExternalData>(() => {
    const profiles = this.profiles();
    const cas = this.cas();
    const cryptotokens = this.cryptotokens();
    return { profiles, cas, cryptotokens };
  });

  private caCertificateProfileOptionsSetter = computed(() => {
    const externalData = this.externalData();
    return this.config.caCertificateProfileOptions(externalData);
  });

  caCertificateProfileOptions = computed<string[]>(() => {
    const setter = this.caCertificateProfileOptionsSetter();
    const formValue = this.formValue();
    return setter(formValue);
  });
  validityYearsOptions = new Array(
    this.config.maxValidityYears - this.config.minValidityYears + 1,
  )
    .fill(undefined)
    .map((_, index) => `${index + this.config.minValidityYears}`);

  certificateMaxValidityYears = toSignal(
    this.form.valueChanges.pipe(
      startWith({}),
      map(this.config.maxCertificateMaxValidityYears),
      distinctUntilChanged(),
      map(maxValue =>
        new Array(maxValue).fill(null).map((_, index) => `${index + 1}`),
      ),
    ),
  );

  private cryptotokenSetter = computed(() => {
    const externalData = this.externalData();
    return this.config.cryptoTokenNameOptions(externalData);
  });

  private formValue: Signal<CaFullFormValue> = toSignal(
    this.form.valueChanges,
    { initialValue: {} },
  );

  cryptoTokenNameOptions = computed(() => {
    const formValue = this.formValue();
    const cryptotokenSetter = this.cryptotokenSetter();
    return cryptotokenSetter(formValue);
  });

  signedByOptions = computed(() => {
    const externalData = this.externalData();
    return this.config.signedByOptions(externalData);
  });

  private updateDisabledFields = this.form.valueChanges.pipe(
    startWith({}),
    combineLatestWith(toObservable(this.externalData)),
    tap(([formValue, externalData]) => {
      Object.values(CaFullFormField).forEach(formField => {
        const disabledReason = this.config.disabledReason[formField]?.(
          formValue,
          { externalData },
        );
        const isDisabled = typeof disabledReason === 'string';
        const control = this.form.controls[formField];
        if (isDisabled) {
          if (!control.disabled) {
            control.disable();
            this.formFieldService.updateControlLabels(formField, {
              disabledTooltip: disabledReason,
            });
          }
        }
        if (!isDisabled) {
          if (control.disabled) {
            control.enable();
          }
        }
      });
    }),
  );

  private clearMaxCertificateValidityIfExceedsMax = this.form.valueChanges.pipe(
    tap(formValue => {
      const certificateMaxValidityYearsValueStr =
        formValue[CaFullFormField.certificateMaxValidityYears];
      if (!certificateMaxValidityYearsValueStr) return;
      const certificateMaxValidityYearsValue = parseInt(
        certificateMaxValidityYearsValueStr,
      );
      if (
        certificateMaxValidityYearsValue <=
        this.config.maxCertificateMaxValidityYears(formValue)
      )
        return;
      this.form.controls[CaFullFormField.certificateMaxValidityYears].setValue(
        null,
      );
    }),
  );

  constructor() {
    this.formFieldService.configure({
      labels: this.labels.inputs,
      options: {
        [CaFullFormField.caType]: FormFieldOptions.select({}),
        [CaFullFormField.cryptoTokenType]: FormFieldOptions.select({
          required: true,
        }),
        [CaFullFormField.cryptoTokenName]: FormFieldOptions.autocomplete({
          required: false,
        }),
        [CaFullFormField.keySpecification]: FormFieldOptions.select({
          required: true,
        }),
        [CaFullFormField.validityYears]: FormFieldOptions.select({
          required: true,
        }),
        [CaFullFormField.certificateMaxValidityYears]: FormFieldOptions.select({
          required: true,
        }),
        [CaFullFormField.caCertificateProfile]: FormFieldOptions.autocomplete({
          required: true,
          strict: true,
        }),
        [CaFullFormField.useLdapDnOrder]: FormFieldOptions.checkbox(),
        [CaFullFormField.dn]: FormFieldOptions.text({ required: true }),
        [CaFullFormField.password]: FormFieldOptions.text({ required: true }),
        [CaFullFormField.signedBy]: FormFieldOptions.autocomplete({
          required: true,
          strict: true,
        }),
        [CaFullFormField.signingAlgo]: FormFieldOptions.select({
          required: true,
        }),
      },
    });
    merge(
      this.updateDisabledFields,
      this.clearMaxCertificateValidityIfExceedsMax,
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  onCancel() {
    this.dialogService.withConfirmation({
      confirmIf: () => this.form.dirty,
      text: this.labels.confirmCancel,
    })(() => this.canceled.emit());
  }

  onCreate() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const dto = formValueToDto(this.form.value);
      this.created.emit(dto);
    }
  }
}
